import React, { useRef, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { stores } from "../context";
import { IType } from "../types/entities";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const CategoryMenu: React.FC = observer(() => {
  const { product } = stores;
  // Use the 'menu' namespace for localization
  const { t, i18n } = useTranslation("menu");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentLang = i18n.language || "pl";
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Safely extract localized category name with fallback options
  const getCategoryName = (type: IType) => {
    if (!type) return "";
    if (typeof type.name === "object" && type.name !== null) {
      const nameObj = type.name as Record<string, string>;
      return (
        nameObj[currentLang] ||
        nameObj.pl ||
        nameObj.en ||
        nameObj.uk ||
        nameObj.de ||
        nameObj.ko ||
        Object.values(nameObj).find((val) => typeof val === "string") ||
        ""
      );
    }
    return String(type.name);
  };

  // Filter categories depending on active menu mode (BAR vs standard MENU)
  const filteredCategories = product.types.filter((cat: any) => {
    const isBarCategory = Boolean(cat.isAlcohol || cat.isDrink);
    if (product.activeMenu === "BAR") {
      return isBarCategory;
    }
    return !isBarCategory;
  });

  // Restore active category from URL parameter on initial mount or refresh
  useEffect(() => {
    const categoryIdFromUrl = searchParams.get("category");

    if (categoryIdFromUrl && product.types.length > 0) {
      const matchedCategory = product.types.find(
        (cat) => String(cat.id) === categoryIdFromUrl
      );
      if (matchedCategory) {
        product.setSelectedType(matchedCategory);
      }
    } else if (!categoryIdFromUrl) {
      // Force reset to "All" if no category parameter exists in URL
      product.setSelectedType(null);
    }
  }, [searchParams, product.types, product]);

  // Handle horizontal scrolling of category list on button clicks
  const handleScroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Select category, update URL search params, and scroll smoothly to target element
  const handleSelectCategory = (type: IType | null) => {
    product.setSelectedType(type);

    // Update URL query string to persist state across page reloads
    if (type) {
      setSearchParams({ category: String(type.id) });
    } else {
      setSearchParams({});
    }

    const scrollToTarget = (id: string) => {
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    };

    if (location.pathname !== "/menu") {
      navigate(type ? `/menu?category=${type.id}` : "/menu");
      if (type) {
        scrollToTarget(`category-${type.id}`);
      }
    } else {
      if (type) {
        scrollToTarget(`category-${type.id}`);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  return (
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 group">
      {/* Scroll Left Button */}
      <button
        type="button"
        aria-label="Scroll Left"
        onClick={() => handleScroll("left")}
        className="hidden md:flex absolute left-1 top-1/2 -translate-y-1/2 z-10 p-1 rounded-full bg-stone-900/90 border border-stone-700 text-stone-300 hover:text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
      >
        <ChevronLeft size={16} />
      </button>

      {/* Horizontal Category Scroll Container */}
      <div
        ref={scrollContainerRef}
        className="flex items-center space-x-2 sm:space-x-3 overflow-x-auto no-scrollbar scroll-smooth py-0.5 px-2"
      >
        {/* "All" Categories Option */}
        <button
          type="button"
          onClick={() => handleSelectCategory(null)}
          className={`px-3 py-1.5 rounded-lg text-xs font-serif tracking-wider uppercase transition-all whitespace-nowrap cursor-pointer flex-shrink-0 ${
            product.selectedType === null
              ? "bg-amber-500 text-stone-950 font-bold shadow-md scale-105"
              : "text-stone-300 hover:text-amber-400 hover:bg-stone-800/50"
          }`}
        >
          {t("categories.All", "Wszystkie")}
        </button>

        {/* Dynamic Category Buttons */}
        {filteredCategories.map((cat: IType) => {
          const isSelected = product.selectedType?.id === cat.id;
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => handleSelectCategory(cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-serif tracking-wider uppercase transition-all whitespace-nowrap cursor-pointer flex-shrink-0 ${
                isSelected
                  ? "bg-amber-500 text-stone-950 font-bold shadow-md scale-105"
                  : "text-stone-300 hover:text-amber-400 hover:bg-stone-800/50"
              }`}
            >
              {getCategoryName(cat)}
            </button>
          );
        })}
      </div>

      {/* Scroll Right Button */}
      <button
        type="button"
        aria-label="Scroll Right"
        onClick={() => handleScroll("right")}
        className="hidden md:flex absolute right-1 top-1/2 -translate-y-1/2 z-10 p-1 rounded-full bg-stone-900/90 border border-stone-700 text-stone-300 hover:text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
});

export default CategoryMenu;
